import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Paperclip } from 'lucide-react';
import { useTaskReturnRevisionModalStore } from '@/entities/task/model/stores/task-return-revision-modal';
import { Alarm } from '@/components/icons/alarmIcon';
import { DocumentIcon } from '@/components/icons/documentIcon';
import { RevisionIcon } from '@/components/icons/revisionIcon';

import {
  approveTask,
  cloneTask,
  completeTask,
  rejectTask,
} from '@/entities/task/api/tasks';
import { buildCommentFiles } from '@/shared/utils/fileToBase64Content';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateTaskCaches } from '@/features/tasks/model/useTaskActions';
import { pad2 } from '@/shared/lib/pad2';

function onlyDigits(val: string) {
  return val.replace(/\D/g, '');
}

function clampIntFromStringDigits(val: string, min: number, max: number) {
  const n = Number(onlyDigits(val));
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, n));
}

function formatHHMMFromDigits(raw: string) {
  const d = onlyDigits(raw).slice(0, 4);
  const hhRaw = d.slice(0, 2);
  const mmRaw = d.slice(2, 4);

  const hh = hhRaw.length ? pad2(clampIntFromStringDigits(hhRaw, 0, 99)) : '00';
  const mm = mmRaw.length ? pad2(clampIntFromStringDigits(mmRaw, 0, 59)) : '00';

  return `${hh}:${mm}`;
}

type AttachedFile = {
  file: File;
  id: string;
};

function makeFileId(f: File) {
  return `${f.name}-${f.size}-${f.lastModified}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export function ReturnForRevisionModal() {
  const isOpen = useTaskReturnRevisionModalStore((s) => s.isOpen);
  const taskId = useTaskReturnRevisionModalStore((s) => s.taskId);
  const isLocked = useTaskReturnRevisionModalStore((s) => s.isLocked);
  const close = useTaskReturnRevisionModalStore((s) => s.close);

  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [timeHHMM, setTimeHHMM] = useState('00:00');

  const [files, setFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const isBusy = isApproving || isRejecting || isExtending;

  function resetLocal() {
    setReason('');
    setTimeHHMM('00:00');
    setFiles([]);
    setIsApproving(false);
    setIsRejecting(false);
    setIsExtending(false);
  }

  function resetAndClose() {
    resetLocal();
    close();
  }

  const extendMinutes = useMemo(() => {
    const [hhStr, mmStr] = timeHHMM.split(':');
    const hh = clampIntFromStringDigits(hhStr ?? '0', 0, 99);
    const mm = clampIntFromStringDigits(mmStr ?? '0', 0, 59);
    return hh * 60 + mm;
  }, [timeHHMM]);

  const canExtend = useMemo(
    () => extendMinutes > 0 && !isBusy,
    [extendMinutes, isBusy]
  );
  const canReturn = useMemo(
    () => reason.trim().length > 0 && !isBusy,
    [reason, isBusy]
  );
  const canApprove = useMemo(() => !isBusy, [isBusy]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (isLocked) {
          return;
        }
        resetAndClose();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow || 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, isLocked]);

  useEffect(() => {
    if (!isOpen) {
      resetLocal();
    }
  }, [isOpen]);

  if (!isOpen || !taskId) {
    return null;
  }

  function pickFiles() {
    fileInputRef.current?.click();
  }

  function onFilesSelected(list: FileList | null) {
    if (!list || list.length === 0) {
      return;
    }

    const next: AttachedFile[] = Array.from(list).map((f) => ({
      file: f,
      id: makeFileId(f),
    }));
    setFiles((prev) => [...prev, ...next]);
  }

  function FileList({ files }: { files: AttachedFile[] }) {
    if (files.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-[5px]">
        {files.map((f) => (
          <button
            key={f.id}
            type="button"
            title={f.file.name}
            className="w-fit max-w-full flex items-center gap-[6px] text-left group"
          >
            <DocumentIcon />
            <span className="text-[14px] leading-[130%] tracking-[-0.83px] font-normal text-white underline underline-offset-2 truncate">
              {f.file.name}
            </span>
          </button>
        ))}
      </div>
    );
  }

  async function handleAccept() {
    if (!taskId || !canApprove) {
      return;
    }

    try {
      setIsApproving(true);
      await approveTask(String(taskId));
      await invalidateTaskCaches(queryClient);
      resetAndClose();
    } catch (e) {
      console.error(e);
      setIsApproving(false);
    }
  }

  async function handleExtend() {
    if (!taskId || !canExtend) {
      return;
    }

    try {
      setIsExtending(true);

      const deadline = new Date(
        Date.now() + extendMinutes * 60 * 1000
      ).toISOString();

      const rejectFiles = await buildCommentFiles(files, 'comment');

      await rejectTask(String(taskId), {
        message: reason.trim(),
        files: rejectFiles,
      });

      await cloneTask(String(taskId), { deadline });
      await completeTask(String(taskId));
      await invalidateTaskCaches(queryClient);

      resetAndClose();
    } catch (e: ANY) {
      console.error(e);
      setIsExtending(false);
    }
  }

  async function handleReturnForRevision() {
    if (!taskId || !canReturn) {
      return;
    }

    try {
      setIsRejecting(true);

      const rejectFiles = await buildCommentFiles(files, 'comment');

      await rejectTask(String(taskId), {
        message: reason.trim(),
        files: rejectFiles,
      });

      await invalidateTaskCaches(queryClient);

      resetAndClose();
    } catch (e) {
      console.error(e);
      setIsRejecting(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[90] font-roboto">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md animate-in fade-in-0 duration-300"
        onClick={isLocked ? undefined : resetAndClose}
        style={{ cursor: isLocked ? 'default' : 'pointer' }}
      />

      <div className="absolute inset-0 grid place-items-center px-[12px]">
        <div
          className="
            w-full max-w-[450px] lg:max-w-[1050px]
            rounded-[40px]
            lg:rounded-[28px]
            overflow-hidden
            ui-glow-desktop
            bg-mobile-header
            border border-[#8AE6FF1F]
            animate-in fade-in-0 zoom-in-95 duration-300 ease-out
          "
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-[31px] lg:px-[24px] lg:py-[40px]">
            <div className="flex items-start justify-between gap-6">
              <h2 className="text-white text-[20px] leading-[130%] tracking-[-0.5px] font-normal">
                Вернуть задачу на доработку.
              </h2>

              <button
                type="button"
                onClick={handleAccept}
                disabled={!canApprove}
                className="
                  shrink-0
                  py-[2.5px]
                  px-[11px]
                  rounded-[8px]
                  text-[14px]
                  leading-[130%]
                  font-medium
                  bg-[#57B792]
                  text-white
                  hover:brightness-110
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                  ui-glow-desktop
                "
              >
                {isApproving ? '...' : 'Принять'}
              </button>
            </div>

            <div className="mt-[24px]">
              <textarea
                placeholder="Укажите причину (обязательное поле)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="
                  w-full
                  min-h-[100px]
                  rounded-[10px]
                  bg-[#8AE6FF1F]
                  border border-[#8AE6FF80]
                  text-white
                  text-[14px]
                  py-[12px]
                  px-[12px]
                  outline-none
                  focus:border-white/30
                  resize-none
                  scrollbar-thin-blue
                "
              />

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <FileList files={files} />
                </div>

                <button
                  type="button"
                  onClick={pickFiles}
                  className="shrink-0 text-white/80 hover:text-white transition mt-[2px]"
                  title="Прикрепить файл"
                  aria-label="Прикрепить файл"
                >
                  <Paperclip size={18} />
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => onFilesSelected(e.target.files)}
              />
            </div>

            <div className="mt-[16px] lg:mt-[24px] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-[16px] lg:gap-[24px]">
              <div className="hidden lg:block text-white text-[16px] leading-[130%] tracking-[-0.5px] font-normal w-[370px]">
                Если необходимо продлить время на задачу, укажи время! Или верни
                задачу без добавления времени
              </div>

              {/* MOBILE layout */}
              <div className="w-full lg:hidden flex flex-col items-center gap-[12px]">
                <div className="w-full flex items-center justify-center gap-[20px]">
                  <div className="flex items-center gap-[10px] rounded-[8px] border border-[#8AE6FF80] bg-[#8AE6FF26] px-3 h-[44px]">
                    <span className="text-white text-[16px] tracking-[-0.83px] leading-[130%] font-normal">
                      Введите время:
                    </span>

                    <input
                      value={timeHHMM}
                      inputMode="numeric"
                      onChange={(e) => {
                        const digits = onlyDigits(e.target.value);
                        setTimeHHMM(formatHHMMFromDigits(digits));
                      }}
                      onFocus={(e) => {
                        requestAnimationFrame(() => {
                          const el = e.currentTarget;
                          const p = el.value.length;
                          el.setSelectionRange(p, p);
                        });
                      }}
                      className="
                        w-[40px]
                        bg-transparent
                        underline
                        rounded-none
                        px-0
                        py-0
                        text-white
                        text-[14px]
                        leading-[130%]
                        tracking-[-0.83px]
                        font-normal
                        tabular-nums
                        outline-none
                        placeholder:text-white/60
                      "
                      aria-label="Время в формате HH:MM"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleExtend}
                    disabled={!canExtend}
                    className="
                      h-[44px]
                      flex items-center gap-[10px]
                      px-3
                      rounded-[8px]
                      text-[16px]
                      tracking-[-0.83px]
                      leading-[130%]
                      font-normal
                      bg-[#8AE6FF26]
                      text-white
                      hover:bg-[#8ae6ff4d]
                      hover:ui-glow-desktop
                      border border-[#8AE6FF80]
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                    "
                  >
                    <Alarm className="shrink-0" />
                    <span>{isExtending ? '...' : 'Продлить'}</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleReturnForRevision}
                  disabled={!canReturn}
                  className="
                    h-[44px]
                    flex items-center gap-[10px]
                    px-[12px]
                    rounded-[8px]
                    text-[16px]
                    tracking-[-0.83px]
                    leading-[130%]
                    font-normal
                    bg-[#8AE6FF26]
                    text-white
                    hover:bg-[#8ae6ff4d]
                    hover:ui-glow-desktop
                    border border-[#8AE6FF80]
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <RevisionIcon className="translate-y-[-2px]" />
                  <span>{isRejecting ? '...' : 'Вернуть на доработку'}</span>
                </button>
              </div>

              {/* DESKTOP layout */}
              <div className="hidden lg:flex items-center justify-start gap-[20px]">
                <div className="flex items-center gap-[10px] rounded-[8px] border border-[#8AE6FF80] bg-[#8AE6FF26] px-3 h-[44px]">
                  <span className="text-white text-[16px] tracking-[-0.83px] leading-[130%] font-normal">
                    Введите время:
                  </span>

                  <input
                    value={timeHHMM}
                    inputMode="numeric"
                    onChange={(e) => {
                      const digits = onlyDigits(e.target.value);
                      setTimeHHMM(formatHHMMFromDigits(digits));
                    }}
                    onFocus={(e) => {
                      requestAnimationFrame(() => {
                        const el = e.currentTarget;
                        const p = el.value.length;
                        el.setSelectionRange(p, p);
                      });
                    }}
                    className="
                      w-[40px]
                      bg-transparent
                      underline
                      rounded-none
                      px-0
                      py-0
                      text-white
                      text-[14px]
                      leading-[130%]
                      tracking-[-0.3px]
                      font-normal
                      tabular-nums
                      outline-none
                      placeholder:text-white/60
                    "
                    aria-label="Время в формате HH:MM"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleExtend}
                  disabled={!canExtend}
                  className="
                    h-[44px]
                    flex items-center gap-[10px]
                    px-3
                    rounded-[8px]
                    text-[16px]
                    tracking-[-0.83px]
                    leading-[130%]
                    font-normal
                    bg-[#8AE6FF26]
                    text-white
                    hover:bg-[#8ae6ff4d]
                    hover:ui-glow-desktop
                    border border-[#8AE6FF80]
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <Alarm className="shrink-0" />
                  <span>{isExtending ? '...' : 'Продлить'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleReturnForRevision}
                  disabled={!canReturn}
                  className="
                    h-[44px]
                    flex items-center gap-[10px]
                    px-[12px]
                    rounded-[8px]
                    text-[16px]
                    tracking-[-0.83px]
                    leading-[130%]
                    font-normal
                    bg-[#8AE6FF26]
                    text-white
                    hover:bg-[#8ae6ff4d]
                    hover:ui-glow-desktop
                    border border-[#8AE6FF80]
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  <RevisionIcon className="translate-y-[-2px]" />
                  <span>{isRejecting ? '...' : 'Вернуть на доработку'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
